import type { ReactNode, Ref } from "react";

import clsx from "clsx";

import { sendTelemetryEvent } from "@/lib/telemetry";
import type { TelemetryProps } from "@Components/Modules/Helpers";
import { requestTrack } from "@Components/Modules/Helpers";

import { ComponentAjax, type IComponentAjax, type IComponentAjaxState } from "../ComponentAjax";
import { type IMainLoadingModule, showMainLoading } from "../Modules/MainLoading";

import * as css from "./Link.module.css";

export enum LinkTarget {
    Self,
    Blank,
}

export enum LinkAppearance {
    Standard,
    LightBlue,
    ButtonMagenta,
    ButtonMagentaRound,
    ButtonLightBlue,
    StandardArrow,
    DarkBlue,
    Magenta,
}

const getLinkAppearanceClassname = (appearance?: LinkAppearance): string => {
    switch (appearance) {
        case LinkAppearance.LightBlue:
            return css.lightBlue;
        case LinkAppearance.ButtonMagenta:
            return css.buttonMagenta;
        case LinkAppearance.ButtonMagentaRound:
            return css.buttonMagentaRound;
        case LinkAppearance.ButtonLightBlue:
            return css.buttonLightBlue;
        case LinkAppearance.DarkBlue:
            return css.darkBlue;
        case LinkAppearance.Magenta:
            return css.magenta;
        case LinkAppearance.StandardArrow:
            return css.standardArrow;
        default:
            return css.standard;
    }
};

const getTarget = ({ navigateUrl, target }: ILink): string | undefined => {
    if (!navigateUrl) {
        return undefined;
    }

    if (target === LinkTarget.Blank) {
        return "_blank";
    }

    return undefined;
};

export interface ILink extends IComponentAjax<boolean>, IMainLoadingModule, TelemetryProps {
    navigateUrl?: string;
    parameterName?: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => boolean | void;
    text?: string;
    textActive?: string;
    target?: LinkTarget;
    appearance?: LinkAppearance;
    attrTitle?: string;
    toggle?: boolean;
    onToggle?: (active: boolean) => void;
    eventBubbling?: boolean;
    forwardRef?: Ref<Element>;
    dataTestId?: string;
}

export interface ILinkState extends IComponentAjaxState {
    active: boolean;
}

export class Link<TProps extends ILink = ILink, TState extends ILinkState = ILinkState> extends ComponentAjax<
    boolean,
    TProps,
    TState
> {
    protected get text(): string | undefined {
        return this.state.active && this.props.textActive ? this.props.textActive : this.props.text;
    }

    protected get title(): string | undefined {
        return this.props.attrTitle || this.text;
    }

    public static defaultProps: ILink = {
        ...ComponentAjax.defaultProps,
        appearance: LinkAppearance.Standard,
        eventBubbling: false,
        hideMainLoading: false,
        target: LinkTarget.Self,
        toggle: false,
    };

    protected isPureComponent: boolean = true;

    protected componentClassName: string = css.linkComponent;

    constructor(props: TProps) {
        super(props);

        this.state = {
            active: this.props.initialData,
            isLoading: false,
        } as TState;
    }

    public render(): ReactNode {
        let linkUrl: string | undefined;
        let role: string | undefined;

        if (this.props.navigateUrl) {
            linkUrl = this.props.navigateUrl;
        } else if (this.props.onClick || this.props.ajaxAction) {
            role = "button";
        }

        const content = this.renderContent();

        if (!content) {
            return null;
        }

        const activeClass = clsx(
            this.state.active && css.active,
            getLinkAppearanceClassname(this.props.appearance),
        );

        const props = {
            ...this.rootDefaultAttributes(this.componentClassName, activeClass),
            id: this.props.clientId ? `${this.props.clientId}-action` : undefined,
            onClick: this.onClick,
            title: this.title,
        };

        const link: JSX.Element = linkUrl ? (
            <a
                {...props}
                href={linkUrl}
                target={getTarget(this.props)}
                data-testid={this.props.dataTestId}
                rel={
                    this.props.navigateUrl &&
                    this.props.navigateUrl[0] !== "/" &&
                    this.props.navigateUrl.indexOf("https://goodie.pl") === -1 &&
                    this.props.navigateUrl.indexOf("bankmillennium") === -1
                        ? "noopener nofollow"
                        : undefined
                }
                ref={this.props.forwardRef as Ref<HTMLAnchorElement>}
            >
                {content}
                {this.renderLoading()}
            </a>
        ) : (
            <div
                {...props}
                role={role}
                ref={this.props.forwardRef as Ref<HTMLDivElement>}
                data-testid={this.props.dataTestId}
            >
                {content}
                {this.renderLoading()}
            </div>
        );

        return (
            <>
                {link}
                {this.props.ajaxAction && super.renderAjaxErrorContainer()}
            </>
        );
    }

    protected ajaxSuccess(response: boolean): void {
        if (this.props.toggle === false) {
            this.setState({ isLoading: false });
            return;
        }

        const active = response === true ? !this.state.active : this.state.active;

        if (this.props.onToggle) {
            this.props.onToggle(active);
        }

        this.setState({ isLoading: false, active });
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    protected onClickWrapper(event: React.MouseEvent<HTMLAnchorElement>): boolean | void {
        if (!this.props.eventBubbling) {
            event.stopPropagation();
        }

        if (this.props.onClick && this.props.onClick(event) === false) {
            event.nativeEvent.stopImmediatePropagation();
            event.nativeEvent.preventDefault();
            event.preventDefault();
            event.stopPropagation();
            return false;
        }

        sendTelemetryEvent(this.props.telemetryCustom);

        requestTrack(this.props.trackingUrl);

        if (this.props.ajaxAction) {
            this.setState({ isLoading: true });

            let param: { [key: string]: boolean } = { active: !this.state.active };

            if (this.props.parameterName) {
                param = {};
                param[this.props.parameterName as string] = !this.state.active;
            }

            this.ajaxRequest(param);
        } else if (this.props.toggle === true) {
            this.setState({ active: !this.state.active });
        }

        if (
            this.props.navigateUrl &&
            event.isDefaultPrevented() === false &&
            !this.props.hideMainLoading &&
            this.props.target !== LinkTarget.Blank
        ) {
            showMainLoading(this.props, event);
        }

        return true;
    }

    protected renderContent(): ReactNode {
        if (this.props.children) {
            return this.props.children;
        }

        switch (this.props.appearance) {
            case LinkAppearance.ButtonMagenta:
            case LinkAppearance.ButtonMagentaRound:
            case LinkAppearance.ButtonLightBlue:
                return <div className={css.buttonWrapper}>{this.text}</div>;

            case LinkAppearance.StandardArrow:
                return (
                    <>
                        {this.text} <span className={css.rightArrow}>{">"}</span>
                    </>
                );
            default:
                return this.text;
        }
    }

    private onClick = (event: React.MouseEvent<HTMLElement>): boolean | void =>
        this.onClickWrapper(event as React.MouseEvent<HTMLAnchorElement>);
}
